import {
  AppModal,
  Form,
  FormSubmitButton,
  SheetFooter,
  SimpleSelectFormField,
  TextAreaFormField,
  TextFormField,
} from "@/components/ui";

import ContactAddressesSection from "@/components/features/contact/ContactAddressesSection/ContactAddressesSection";
import ContactEmailsSection from "@/components/features/contact/ContactEmailsSection/ContactEmailsSection";
import ContactFaxesSection from "@/components/features/contact/ContactFaxesSection/ContactFaxesSection";
import ContactNotesSection from "@/components/features/contact/ContactNotesSection/ContactNotesSection";
import ContactPhoneNumbersSection from "@/components/features/contact/ContactPhoneNumbersSection/ContactPhoneNumbersSection";
import ContactTagConnectionsSection from "@/components/features/contact/ContactTagConnectionsSection/ContactTagConnectionsSection";
import ContactWebsitesSection from "@/components/features/contact/ContactWebsitesSection/ContactWebsitesSection";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import { useContactInvalidation } from "@/hooks/use-contact-invalidation";
import {
  retrieveContactQueryOptions,
  useCreateContact,
} from "@/lib/data/react-query";
import {
  isPossibleDuplicateResponse,
  type PossibleDuplicateContact,
} from "@/lib/data/requests";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type APIResponse, type Contact } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { isAnyDirtyInFieldTree } from "@/lib/utils/react-hook-form/dirty-field-tree";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import isEqual from "lodash/isEqual";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useForm, useFormContext, useWatch } from "react-hook-form";
import toast from "react-hot-toast";
import type { z } from "zod";
import { ContactFormLivePreview } from "./ContactFormLivePreview";
import { ContactSubitemSection } from "./ContactSubitemSection";
import { createContactFormSchema } from "./contactFormSchema";
import { ContactIndividualFormSection } from "./ContactIndividualFormSection";
import { ContactOrganizationFormSection } from "./ContactOrganizationFormSection";
import { DuplicateContactSelectionModal } from "./DuplicateContactSelectionModal";
import { useDeepDirtyBaseline } from "./useDeepDirtyBaseline";

export { createContactFormSchema } from "./contactFormSchema";

/** Stable id so `AppModal` footer buttons can `form=` submit the embedded create form. */
export const EMBEDDED_CREATE_CONTACT_FORM_ID =
  "lw-embedded-create-contact-form";
const STANDALONE_CREATE_CONTACT_FORM_ID = "lw-create-contact-form";

interface AddContactSlideoverProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  creatingMore?: boolean;
  /** When async, loading stays on until this resolves (e.g. matter connection created). */
  afterCreateFn?: (res: APIResponse<Contact>) => void | Promise<void>;
  /** False when a parent shows one combined success toast after `afterCreateFn`. */
  showCreateSuccessToast?: boolean;
  activeContactId?: string;
  activeContact?: Contact;
  hideSubItems?: boolean;
  hasEditPermission?: boolean;
  defaultValues?: Partial<z.infer<typeof createContactFormSchema>>;
  /**
   * Render form only (no Slideover). Parent supplies the shell — e.g. animated
   * select → create inside one contact picker modal.
   */
  contentOnly?: boolean;
  /** When `contentOnly` inside contact picker `AppModal`, footer is rendered on the modal — hide inline footer. */
  externalModalFooter?: boolean;
  onShellDirtyChange?: (dirty: boolean) => void;
  onShellLoadingChange?: (loading: boolean) => void;
  onShellCanSubmitChange?: (canSubmit: boolean) => void;
}

const defaultFormValues = {
  individualDateOfBirth: undefined,
  individualDeceased: false,
  individualExpertWitnessSpecialization: [],
  individualFirstName: null,
  individualGender: null,
  individualPrimaryLanguage: null,
  individualSecondaryLanguage: [],
  individualLastName: null,
  individualMiddleName: "",
  individualNickname: "",
  individualMinor: null,
  individualSmsConsent: null,
  individualTimezone: null,
  individualTitle: null,
  individualRace: [],
  title: "",
  phones: [],
  emails: [],
  addresses: [],
  faxes: [],
  websites: [],
  notes: [],
  tagConnections: [],
  individualSocialSecurityNumber: "",
  individualDriverLicenseNumber: "",
  individualDriverLicenseState: "",
  individualSubTypes: null,
  organizationFoundingYear: null,
  organizationMedicalProviderAcceptsLiens: null,
  organizationMedicalProviderSpecialization: [],
  organizationName: null,
  organizationTaxId: null,
  organizationSubTypes: null,
  groupName: null,
  groupDescription: "",
  preferredContactMethod: null,
  type: "individual" as "individual" | "organization" | "group",
};

/**
 * Forwards "has unsaved edits" to the contact picker shell (modal footer).
 * Must render under `<Form {...createForm}>` so we read the embedded create form only
 * (picker is often nested under another RHF `<Form>`).
 *
 * The embedded create shell intentionally suppresses mount-time "dirty" noise until
 * its re-baseline has settled, but genuine field edits must still flip the parent
 * modal immediately so close confirmation works even if the user types and clicks
 * close right away.
 */
function CreateContactFormShellStateBridge({
  pickerShellReady,
  dirty,
  onShellDirtyChange,
}: {
  pickerShellReady: boolean;
  dirty: boolean;
  onShellDirtyChange: (dirty: boolean) => void;
}) {
  const { formState } =
    useFormContext<z.infer<typeof createContactFormSchema>>();
  const hasDirtyFields = isAnyDirtyInFieldTree(formState.dirtyFields);

  useLayoutEffect(() => {
    if (!pickerShellReady && !hasDirtyFields) {
      onShellDirtyChange(false);
      return;
    }
    onShellDirtyChange(dirty || hasDirtyFields);
  }, [dirty, hasDirtyFields, onShellDirtyChange, pickerShellReady]);

  return null;
}

export const AddContactSlideover = ({
  open,
  setOpen,
  activeContactId,
  activeContact,
  afterCreateFn,
  hideSubItems,
  defaultValues,
  contentOnly = false,
  externalModalFooter = false,
  showCreateSuccessToast = true,
  onShellDirtyChange,
  onShellLoadingChange,
  onShellCanSubmitChange,
}: AddContactSlideoverProps) => {
  const queryClient = useQueryClient();
  const { data: contactData } = useQuery(
    retrieveContactQueryOptions(activeContactId),
  );

  const { hasActionPermission } = useUser();

  const contact = activeContact ?? contactData?.data;

  const createForm = useForm<z.infer<typeof createContactFormSchema>>({
    resolver: zodResolver(createContactFormSchema),
    defaultValues: { ...defaultFormValues, ...defaultValues },
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const defaultValuesRef = useRef(defaultValues);
  defaultValuesRef.current = defaultValues;
  const watchedCreateFormValues = useWatch({
    control: createForm.control,
  });
  const normalizedCreateFormValues = useMemo(
    () => ({ ...defaultFormValues, ...watchedCreateFormValues }),
    [watchedCreateFormValues],
  );
  const {
    isDirty: isCreateFormDirty,
    setBaselineSnapshot: setCreateDirtyBaseline,
    clearBaseline: clearCreateDirtyBaseline,
  } = useDeepDirtyBaseline({
    open,
    currentValues: normalizedCreateFormValues,
  });
  const canSubmitCreateForm = !isEqual(
    normalizedCreateFormValues,
    defaultFormValues,
  );

  /** Tracks `open` across runs so we can run a one-time re-baseline when the create form is shown. */
  const createFormOpenPrevRef = useRef(false);
  const standaloneCreateFormOpenPrevRef = useRef(false);

  /**
   * In the contact picker (`contentOnly`), the shell footer reads `onShellDirtyChange` to show
   * Close vs Cancel. Until the async re-baseline `reset` runs, we must not forward dirty state.
   * After that, we combine a deep `dirtyFields` walk (so nested + field arrays count) with
   * `isDirty` so add/remove of emails, phones, etc. enables the shell “Cancel” / save affordance
   * without missing nested dirtiness that `Object.keys(dirtyFields)` can skip.
   */
  const [pickerShellReady, setPickerShellReady] = useState(false);

  const emails = [
    ...(createForm.watch("emails") ?? []),
    ...(contact?.emails ?? []),
  ];
  const addresses = [
    ...(createForm.watch("addresses") ?? []),
    ...(contact?.addresses ?? []),
  ];
  const phones = [
    ...(createForm.watch("phones") ?? []),
    ...(contact?.phones ?? []),
  ];
  const faxes = [
    ...(createForm.watch("faxes") ?? []),
    ...(contact?.faxes ?? []),
  ];
  const websites = [
    ...(createForm.watch("websites") ?? []),
    ...(contact?.websites ?? []),
  ];
  const notes = [
    ...(createForm.watch("notes") ?? []),
    ...(contact?.notes ?? []),
  ];

  const invalidateContact = useContactInvalidation();
  const createContact = useCreateContact({
    invalidateOnSettled: false,
    showSuccessToast: false,
  });
  const [submitLifecycleLoading, setSubmitLifecycleLoading] = useState(false);
  const isFormLoading = createContact.isPending || submitLifecycleLoading;

  const [creatingMore] = useState(false);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [pendingFormValues, setPendingFormValues] = useState<z.infer<
    typeof createContactFormSchema
  > | null>(null);
  const [duplicateContacts, setDuplicateContacts] = useState<
    PossibleDuplicateContact[]
  >([]);
  const handleCreateContact = async (
    data: z.infer<typeof createContactFormSchema>,
    createDespitePossibleDuplicate = false,
  ) => {
    setSubmitLifecycleLoading(true);
    let possibleDuplicatesFound = false;
    const {
      addresses = [],
      emails = [],
      faxes = [],
      notes = [],
      phones = [],
      tagConnections = [],
      websites = [],
      ...contactPayload
    } = data;

    const hasAddressData = (address: (typeof addresses)[number]) =>
      [
        address.street1,
        address.street2,
        address.city,
        address.state,
        address.zipCode,
        address.description,
        address.label,
      ].some((value) => (value ?? "").toString().trim().length > 0) ||
      !["", "United States"].includes(address.country.trim());

    try {
      const res = await createContact.mutateAsync({
        ...contactPayload,
        addresses: addresses
          .filter(hasAddressData)
          .map(
            ({
              city,
              country,
              county,
              description,
              isPrimary,
              label,
              state,
              street1,
              street2,
              zipCode,
            }) => ({
              city,
              country,
              county,
              description,
              isPrimary,
              label,
              state,
              street1: street1.trim() || null,
              street2,
              zipCode,
            }),
          ),
        createDespitePossibleDuplicate,
        emails: emails.map(({ address, description, isPrimary, label }) => ({
          address,
          description,
          isPrimary,
          label,
        })),
        individualDateOfBirth:
          data.individualDateOfBirth?.toString() ?? undefined,
        faxes: faxes.map(
          ({ description, extension, isPrimary, label, number }) => ({
            description,
            extension,
            isPrimary,
            label,
            number,
          }),
        ),
        notes: notes
          .filter((note) => (note.message?.trim().length ?? 0) > 0)
          .map(({ draft, message }) => ({ draft, message })),
        phones: phones.map(
          ({ description, extension, isPrimary, label, number }) => ({
            description,
            extension,
            isPrimary,
            label,
            number,
          }),
        ),
        tagConnections: tagConnections.map(({ tagId }) => ({ tagId })),
        websites: websites.map(({ address, description, label }) => ({
          address,
          description,
          label,
        })),
      });

      if (isPossibleDuplicateResponse(res)) {
        possibleDuplicatesFound = true;
        setPendingFormValues(data);
        setDuplicateContacts(res.data);
        setDuplicateModalOpen(true);
        setSubmitLifecycleLoading(false);
        return;
      }

      const createdContactId = res.data.id;

      if (!creatingMore) {
        setOpen(false);
      }

      const refreshedContact = await queryClient
        .fetchQuery(retrieveContactQueryOptions(createdContactId))
        .then((contactRes) => contactRes.data)
        .catch(() => res.data);

      if (!creatingMore) {
        await Promise.resolve(
          afterCreateFn?.({
            ...res,
            data: refreshedContact,
          }),
        );
      } else {
        createForm.reset();
      }

      await invalidateContact();
      if (showCreateSuccessToast) {
        toast.success("Contact created successfully");
      }

      if (!creatingMore) {
        return;
      }

      setSubmitLifecycleLoading(false);
    } catch (error) {
      setSubmitLifecycleLoading(false);
      throw error;
    } finally {
      if (!possibleDuplicatesFound) {
        setPendingFormValues(null);
        setDuplicateContacts([]);
        setDuplicateModalOpen(false);
      }
    }
  };

  const onSubmit = async (data: z.infer<typeof createContactFormSchema>) => {
    await handleCreateContact(data);
  };

  useEffect(() => {
    if (!open) {
      createForm.reset({
        ...defaultFormValues,
        ...defaultValuesRef.current,
      });
      clearCreateDirtyBaseline();
      setPickerShellReady(false);
      setDuplicateModalOpen(false);
      setPendingFormValues(null);
      setDuplicateContacts([]);
      setSubmitLifecycleLoading(false);
    }
  }, [clearCreateDirtyBaseline, open, createForm]);

  useEffect(() => {
    if (contentOnly) {
      return;
    }
    const wasOpen = standaloneCreateFormOpenPrevRef.current;
    standaloneCreateFormOpenPrevRef.current = open;
    if (!open || wasOpen) {
      return;
    }
    const baseline = {
      ...defaultFormValues,
      ...defaultValuesRef.current,
    };
    createForm.reset(baseline);
    setCreateDirtyBaseline(baseline);
  }, [contentOnly, createForm, open, setCreateDirtyBaseline]);

  /**
   * Embedded create (e.g. contact picker modal) mounts many controlled fields. Their first
   * register/sync pass can set `isDirty` even though the user has not changed anything, which
   * makes `AppModal` show an "unsaved changes" prompt. Re-baseline right after the shell opens
   * so the dirty snapshot matches the current values.
   */
  /**
   * Re-baselines the embedded form when the shell opens. `defaultValues` must be read from a
   * ref here: the parent often passes a new object each render, and listing `defaultValues` in
   * the dependency array can retrigger this effect while `open` stays true, cancel the deferred
   * `setPickerShellReady(true)`, and leave the shell stuck thinking nothing is saveable.
   */
  useEffect(() => {
    if (!contentOnly) {
      return;
    }
    const wasOpen = createFormOpenPrevRef.current;
    createFormOpenPrevRef.current = open;
    if (!open || wasOpen) {
      return;
    }
    const baseline = {
      ...defaultFormValues,
      ...defaultValuesRef.current,
    };
    createForm.reset(baseline);
    setCreateDirtyBaseline(baseline);
    setPickerShellReady(false);
    // Defer only the shell-ready flag so mount-time field registration settles
    // before we start showing the parent modal's unsaved-changes state.
    let innerId: ReturnType<typeof setTimeout> | undefined;
    const outerId = globalThis.setTimeout(() => {
      innerId = globalThis.setTimeout(() => {
        setPickerShellReady(true);
      }, 0);
    }, 0);
    return () => {
      clearTimeout(outerId);
      if (innerId !== undefined) {
        clearTimeout(innerId);
      }
    };
  }, [contentOnly, open, createForm, setCreateDirtyBaseline]);

  useLayoutEffect(() => {
    if (!contentOnly || !onShellLoadingChange) {
      return;
    }
    onShellLoadingChange(isFormLoading);
  }, [contentOnly, isFormLoading, onShellLoadingChange]);

  useLayoutEffect(() => {
    if (!contentOnly || !onShellCanSubmitChange) {
      return;
    }
    onShellCanSubmitChange(canSubmitCreateForm);
  }, [canSubmitCreateForm, contentOnly, onShellCanSubmitChange]);

  useEffect(() => {
    if (!open && contentOnly) {
      onShellDirtyChange?.(false);
      onShellLoadingChange?.(false);
      onShellCanSubmitChange?.(false);
    }
  }, [
    open,
    contentOnly,
    onShellDirtyChange,
    onShellLoadingChange,
    onShellCanSubmitChange,
  ]);

  const type = createForm.watch("type");
  const isGroupContact = type === "group";
  const shouldUseExternalFooter = !contentOnly || externalModalFooter;
  const createContactFormId = shouldUseExternalFooter
    ? contentOnly && externalModalFooter
      ? EMBEDDED_CREATE_CONTACT_FORM_ID
      : STANDALONE_CREATE_CONTACT_FORM_ID
    : undefined;
  const modalFooter = (
    <div className="flex w-full justify-end gap-2">
      <FormCancelButton setOpen={setOpen} isDirty={isCreateFormDirty} />
      <FormSubmitButton
        loading={isFormLoading}
        overrideDisabled
        disabled={!canSubmitCreateForm}
        submitFormId={createContactFormId}
      />
    </div>
  );

  const createFormBody = (
    <Form {...createForm}>
      {contentOnly && onShellDirtyChange ? (
        <CreateContactFormShellStateBridge
          pickerShellReady={pickerShellReady}
          dirty={isCreateFormDirty}
          onShellDirtyChange={onShellDirtyChange}
        />
      ) : null}
      <form
        id={createContactFormId}
        onSubmit={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          await createForm.handleSubmit(onSubmit)();
        }}
        className="flex h-full max-h-[min(82vh,800px)] min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden lg:flex-row"
      >
        <aside
          className={cn(
            "border-border flex max-h-[min(40vh,320px)] min-h-0 shrink-0 flex-col overflow-hidden border-b",
            "lg:max-h-[min(88vh,920px)] lg:w-[min(100%,420px)] lg:max-w-[420px] lg:self-stretch lg:border-r lg:border-b-0",
          )}
        >
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pt-3 pb-4">
            <p className="text-muted-foreground mb-2 text-xs font-medium tracking-wide uppercase">
              Preview
            </p>
            <ContactFormLivePreview />
          </div>
        </aside>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          {isFormLoading && <FormLoadingOverlay />}
          <div
            className={cn(
              "min-h-0 flex-1 overflow-y-auto overscroll-contain lg:max-h-[min(88vh,920px)]",
              contentOnly && externalModalFooter && "pb-6",
            )}
          >
            <fieldset className={cn(slideoverFieldsetClassName, "mt-0")}>
              <SimpleSelectFormField
                register={createForm.register("type")}
                label="Contact type*"
                items={["individual", "organization", "group"]}
              />
              {type === "individual" && <ContactIndividualFormSection />}
              {type === "organization" && <ContactOrganizationFormSection />}
              {isGroupContact && (
                <>
                  <TextFormField
                    register={createForm.register("groupName")}
                    label="Group name*"
                    dataTestId="contact-group-name"
                  />
                  <div className="col-span-2">
                    <TextAreaFormField
                      register={createForm.register("groupDescription")}
                      label="Group description"
                    />
                  </div>
                </>
              )}
            </fieldset>
            {!hideSubItems && !isGroupContact && (
              <div className="col-span-full mb-4 px-2 py-4">
                <ContactSubitemSection
                  title="Contact emails"
                  count={emails.length}
                >
                  <ContactEmailsSection
                    hideLabel
                    parentForm={createForm}
                    emails={contact?.emails}
                    contactId={contact?.id ?? ""}
                    hasCreatePermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contactEmail,
                      ACTION_PERMISSION_VALUES.create,
                    )}
                    hasEditPermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contactEmail,
                      ACTION_PERMISSION_VALUES.update,
                    )}
                    hasDeletePermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contactEmail,
                      ACTION_PERMISSION_VALUES.delete,
                    )}
                    organizationSubTypes={
                      createForm.watch("organizationSubTypes") ?? undefined
                    }
                    type={createForm.watch("type") ?? undefined}
                  />
                </ContactSubitemSection>

                <ContactSubitemSection
                  title="Contact addresses"
                  count={addresses.length}
                >
                  <ContactAddressesSection
                    hideLabel
                    collapseMultiple
                    parentForm={createForm}
                    addresses={contact?.addresses}
                    contactId={contact?.id ?? ""}
                    hasCreatePermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contactAddress,
                      ACTION_PERMISSION_VALUES.create,
                    )}
                    hasEditPermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contactAddress,
                      ACTION_PERMISSION_VALUES.update,
                    )}
                    hasDeletePermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contactAddress,
                      ACTION_PERMISSION_VALUES.delete,
                    )}
                  />
                </ContactSubitemSection>

                <ContactSubitemSection
                  title="Contact phones"
                  count={phones.length}
                >
                  <ContactPhoneNumbersSection
                    hideLabel
                    parentForm={createForm}
                    phoneNumbers={contact?.phones}
                    contactId={contact?.id ?? ""}
                    hasCreatePermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contactPhone,
                      ACTION_PERMISSION_VALUES.create,
                    )}
                    hasEditPermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contactPhone,
                      ACTION_PERMISSION_VALUES.update,
                    )}
                    hasDeletePermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contactPhone,
                      ACTION_PERMISSION_VALUES.delete,
                    )}
                    organizationSubTypes={
                      createForm.watch("organizationSubTypes") ?? undefined
                    }
                    type={createForm.watch("type") ?? undefined}
                  />
                </ContactSubitemSection>

                <ContactSubitemSection
                  title="Contact faxes"
                  count={faxes.length}
                >
                  <ContactFaxesSection
                    hideLabel
                    parentForm={createForm}
                    faxes={contact?.faxes}
                    contactId={contact?.id ?? ""}
                    hasCreatePermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contactFax,
                      ACTION_PERMISSION_VALUES.create,
                    )}
                    hasEditPermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contactFax,
                      ACTION_PERMISSION_VALUES.update,
                    )}
                    hasDeletePermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contactFax,
                      ACTION_PERMISSION_VALUES.delete,
                    )}
                  />
                </ContactSubitemSection>

                <ContactSubitemSection
                  title="Contact websites"
                  count={websites.length}
                >
                  <ContactWebsitesSection
                    hideLabel
                    compact
                    parentForm={createForm}
                    websites={contact?.websites}
                    contactId={contact?.id ?? ""}
                    hasCreatePermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contactWebsite,
                      ACTION_PERMISSION_VALUES.create,
                    )}
                    hasEditPermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contactWebsite,
                      ACTION_PERMISSION_VALUES.update,
                    )}
                    hasDeletePermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contactWebsite,
                      ACTION_PERMISSION_VALUES.delete,
                    )}
                  />
                </ContactSubitemSection>

                <ContactSubitemSection
                  title="Contact notes"
                  count={notes.length}
                >
                  <ContactNotesSection
                    hideLabel
                    parentForm={createForm}
                    notes={contact?.notes}
                    comments={contact?.noteComments}
                    contactId={contact?.id ?? ""}
                    hasCreatePermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contact,
                      ACTION_PERMISSION_VALUES.create,
                    )}
                    hasEditPermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contact,
                      ACTION_PERMISSION_VALUES.update,
                    )}
                    hasDeletePermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contact,
                      ACTION_PERMISSION_VALUES.delete,
                    )}
                  />
                </ContactSubitemSection>

                {contact && (
                  <div className="mt-4">
                    <ContactTagConnectionsSection
                      tagConnections={contact.tagConnections}
                      parentForm={createForm}
                      contactId={contact.id}
                      hasCreatePermission={hasActionPermission(
                        ACTION_PERMISSION_KEYS.contactTagConnection,
                        ACTION_PERMISSION_VALUES.create,
                      )}
                      hasDeletePermission={hasActionPermission(
                        ACTION_PERMISSION_KEYS.contactTagConnection,
                        ACTION_PERMISSION_VALUES.delete,
                      )}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
          {contentOnly && !externalModalFooter ? (
            <SheetFooter addSlideoverStyles={false}>
              <div className="border-border bg-card sticky bottom-0 flex w-full shrink-0 justify-end gap-2 border-t px-4 py-3 sm:px-5">
                <FormCancelButton
                  setOpen={setOpen}
                  isDirty={isCreateFormDirty}
                />
                <FormSubmitButton
                  loading={isFormLoading}
                  overrideDisabled
                  disabled={!canSubmitCreateForm}
                />
              </div>
            </SheetFooter>
          ) : null}
        </div>
      </form>
    </Form>
  );

  return (
    <>
      {contentOnly ? (
        createFormBody
      ) : (
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title="Create contact"
          description="Create a new contact."
          isDirty={isCreateFormDirty}
          loading={isFormLoading}
          size="3xl"
          className="w-[calc(100vw-2rem)]"
          bodyContained
          bodyClassName="min-h-0"
          footer={modalFooter}
        >
          {createFormBody}
        </AppModal>
      )}
      <DuplicateContactSelectionModal
        contacts={duplicateContacts}
        open={duplicateModalOpen}
        onCreateAsNew={async () => {
          const values = pendingFormValues;
          if (!values) {
            return;
          }
          setDuplicateModalOpen(false);
          setDuplicateContacts([]);
          setPendingFormValues(null);
          await handleCreateContact(values, true);
        }}
        onGoBack={() => {
          setPendingFormValues(null);
          setDuplicateContacts([]);
          setDuplicateModalOpen(false);
        }}
        onUseContact={async (contactId) => {
          try {
            const existingContactResponse = await queryClient.fetchQuery(
              retrieveContactQueryOptions(contactId),
            );
            await Promise.resolve(afterCreateFn?.(existingContactResponse));
            setDuplicateModalOpen(false);
            setDuplicateContacts([]);
            setPendingFormValues(null);
            setOpen(false);
          } catch {
            toast.error("Unable to use the selected contact");
          }
        }}
      />
    </>
  );
};
